import { ref } from 'vue'
import { addDays } from 'date-fns'
import httpClient from '../utils/httpClient'
import { cacheManager } from '../utils/cacheManager'
import { DEFAULT_LLM, DEFAULT_MODELS } from '@/utils/constants'
import { EVENT_VIEW_MODES } from '@/utils/eventTypes'

export interface ForecastMetadata {
  city_name?: string
  country_code?: string
  timezone?: string
  latitude?: number
  longitude?: number
  sunrise?: string
  sunset?: string
}

export interface ForecastData {
  text: string
  csvData: string
  loading: boolean
  error: string | null
  metadata: ForecastMetadata | null
  attribution: {
    llm: string
    weatherModels: string
  } | null
}

export const DAY_KEYS = ['day0', 'day1', 'day2', 'day3', 'day4', 'day5', 'day6'] as const
export type DayKey = typeof DAY_KEYS[number]

// View modes split in two: those rendering forecast data and those rendering
// events. displayContent() routes on this — anything not a forecast mode is
// sent to /api/events as an event_type, so a mode missing from
// FORECAST_VIEW_MODES silently fires a request the events API must reject.
// The event half is imported rather than restated and VALID_VIEW_MODES is
// derived; duplicated lists drifting apart is exactly what caused #180.
const FORECAST_VIEW_MODES = ['main', 'table', 'icon'] as const
export const VALID_VIEW_MODES = [...FORECAST_VIEW_MODES, ...EVENT_VIEW_MODES] as const
export const isForecastMode = (mode: string): boolean => FORECAST_VIEW_MODES.includes(mode as any)

export function createEmptyForecast(): ForecastData {
  return { text: '', csvData: '', loading: false, error: null, metadata: null, attribution: null }
}

interface WeatherDataOptions {
  getLocation: () => string
  getLanguage: () => string
  getCountryCode: () => string | undefined
  getModelString: () => string | undefined
  getSelectedLLM: () => string | undefined
  getTextLength: () => number
  // Fixed length for the main-view summary text. When provided AND it
  // differs from getTextLength, fetchWeeklyForecast does a second
  // /api/weekly-text call in parallel and stores the result in
  // summaryTextsArray. If the user's text length matches, we skip the
  // extra call — template code should prefer the main forecast text in
  // that case (see Forecast.vue).
  getSummaryLength?: () => number | null
  getSelectedAudience: () => string | undefined
  getViewMode: () => string
  getActiveTab: () => number
  onTimezoneUpdate: (timezone: string) => void
  formatLLMName: (llm: string) => string
  formatWeatherModels: (models: string) => string
}

export function useWeatherData(opts: WeatherDataOptions) {
  const forecastsArray = ref<ForecastData[]>(
    Array.from({ length: DAY_KEYS.length }, () => createEmptyForecast())
  )
  // Per-day summary text at getSummaryLength (independent of the primary
  // text length). Populated lazily by fetchWeeklyForecast when a summary
  // length is configured and differs from the primary length.
  const summaryTextsArray = ref<string[]>(Array(DAY_KEYS.length).fill(''))
  const currentEvents = ref<any>(null)
  const currentEventType = ref<string | null>(null)
  const isEventsLoading = ref(false)
  const eventsError = ref<string | null>(null)
  // True while the weekly-text (Phase 2) request is in flight. Tab switches
  // during this window must NOT trigger per-day /api/forecast fetches — Phase 2
  // will populate every day's text at once.
  const isWeeklyTextInFlight = ref(false)

  const getCurrentDateString = (): string => {
    // Prefer the location's timezone, but fall back to the browser's IANA
    // zone rather than throwing — the previous "Timezone not set" error
    // was reachable on a fresh auto-detect (settings.location set without
    // ever touching Location.vue), and once raised it set forecast.error
    // which in turn outranks every viewMode branch in Forecast.vue,
    // making the header wordmark appear non-functional.
    const stored = localStorage.getItem('locationTimezone')
    const timezone = (stored && stored.trim())
      ? stored
      : Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    const baseDate = new Date()
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit'
    })
    const todayStr = formatter.format(baseDate)
    const [year, month, day] = todayStr.split('-').map(Number)
    const today = new Date(Date.UTC(year, month - 1, day))
    const targetDate = addDays(today, opts.getActiveTab())
    return targetDate.toISOString().split('T')[0]
  }

  const getCacheKey = (): string => {
    const dateStr = getCurrentDateString()
    const location = opts.getLocation()
    const language = opts.getLanguage()
    const countryCode = (opts.getCountryCode() || '').trim()
    if (isForecastMode(opts.getViewMode())) {
      const model = (opts.getModelString() || DEFAULT_MODELS || '').trim()
      const llm = (opts.getSelectedLLM() || DEFAULT_LLM || '').trim()
      return `wital_weather_${location}_${countryCode}_${dateStr}_${language}_${opts.getTextLength()}_${opts.getSelectedAudience() || 'adult'}_${model}_${llm}`
    } else {
      return `wital_events_${opts.getViewMode()}_${location}_${countryCode}_${dateStr}_${language}`
    }
  }

  const hydrateMetadata = (dayIndex: number, data: any) => {
    if (data.location_info) {
      forecastsArray.value[dayIndex].metadata = {
        city_name: data.location_info.city_name,
        country_code: data.location_info.country_code,
        timezone: data.location_info.timezone,
        latitude: data.location_info.latitude,
        longitude: data.location_info.longitude,
        sunrise: data.sunrise,
        sunset: data.sunset
      }
      opts.onTimezoneUpdate(data.location_info.timezone)
    } else if (data.city_name || data.timezone) {
      forecastsArray.value[dayIndex].metadata = {
        city_name: data.city_name,
        country_code: data.country_code,
        timezone: data.timezone,
        latitude: data.latitude,
        longitude: data.longitude,
        sunrise: data.sunrise,
        sunset: data.sunset
      }
      opts.onTimezoneUpdate(data.timezone)
    }
  }

  const buildAttribution = () => ({
    llm: opts.formatLLMName(opts.getSelectedLLM() || DEFAULT_LLM),
    weatherModels: opts.formatWeatherModels(opts.getModelString() || DEFAULT_MODELS)
  })

  const displayContent = async () => {
    let cacheKey: string | null = null
    try {
      cacheKey = getCacheKey()
      const cachedData = cacheManager.get(cacheKey)
      if (cachedData) {
        if (import.meta.env.DEV) console.log(`💾 Cache hit for ${cacheKey}`)
        if (isForecastMode(opts.getViewMode())) {
          const dayIndex = opts.getActiveTab()
          try {
            forecastsArray.value[dayIndex].text = (cachedData.message || '').toString().trim()
            forecastsArray.value[dayIndex].csvData = (cachedData.weather_data || '').toString()
            hydrateMetadata(dayIndex, cachedData)
            forecastsArray.value[dayIndex].attribution = buildAttribution()
          } finally {
            forecastsArray.value[dayIndex].loading = false
            forecastsArray.value[dayIndex].error = null
          }
        } else {
          currentEvents.value = cachedData
          currentEventType.value = opts.getViewMode()
        }
        return
      }
      if (import.meta.env.DEV) console.log(`🌐 Fetching fresh data for ${cacheKey}`)
    } catch (e: any) {
      console.error('❌ Error while preparing cache key:', e?.message || e)
      if (isForecastMode(opts.getViewMode())) {
        const dayIndex = opts.getActiveTab()
        forecastsArray.value[dayIndex].error = e?.message || 'Failed to load content'
        forecastsArray.value[dayIndex].loading = false
      } else {
        currentEvents.value = null
        isEventsLoading.value = false
        eventsError.value = e?.message || 'Failed to load content'
      }
      return
    }

    if (isForecastMode(opts.getViewMode())) {
      const dayIndex = opts.getActiveTab()
      // Phase 2 (weekly-text) is still running — it will populate every day at
      // once, so don't issue a redundant single-day LLM fetch here and don't
      // flip loading back to true (which would hide the chart/table that Phase
      // 1 already rendered).
      if (isWeeklyTextInFlight.value) return
      forecastsArray.value[dayIndex].loading = true
      forecastsArray.value[dayIndex].error = null
    } else {
      isEventsLoading.value = true
      eventsError.value = null
    }

    try {
      if (isForecastMode(opts.getViewMode())) {
        const weatherParams = {
          location: opts.getLocation(),
          country_code: (opts.getCountryCode() && opts.getCountryCode()!.trim()) ? opts.getCountryCode()!.trim() : undefined,
          date: getCurrentDateString(),
          language: opts.getLanguage(),
          model: opts.getModelString() || DEFAULT_MODELS,
          length: opts.getTextLength(),
          audience: opts.getSelectedAudience() || 'adult',
          llm: opts.getSelectedLLM() || DEFAULT_LLM
        }
        const response = await httpClient.get('/api/forecast', { params: weatherParams })
        cacheManager.set(cacheKey!, response.data)
        const dayIndex = opts.getActiveTab()
        forecastsArray.value[dayIndex].text = (response.data.message || '').toString().trim()
        forecastsArray.value[dayIndex].csvData = (response.data.weather_data || '').toString()
        hydrateMetadata(dayIndex, response.data)
        forecastsArray.value[dayIndex].attribution = buildAttribution()
        forecastsArray.value[dayIndex].loading = false
        forecastsArray.value[dayIndex].error = null
      } else {
        const eventParams = {
          event_type: opts.getViewMode(),
          location: opts.getLocation(),
          country_code: (opts.getCountryCode() && opts.getCountryCode()!.trim()) ? opts.getCountryCode()!.trim() : undefined,
          date: getCurrentDateString(),
          language_code: opts.getLanguage(),
          llm: opts.getSelectedLLM() || DEFAULT_LLM
        }
        const response = await httpClient.get('/api/events', { params: eventParams })
        cacheManager.set(cacheKey!, response.data)
        currentEvents.value = response.data
        currentEventType.value = opts.getViewMode()
        isEventsLoading.value = false
      }
    } catch (err: any) {
      console.error('❌ Error fetching data:', err)
      if (isForecastMode(opts.getViewMode())) {
        const dayIndex = opts.getActiveTab()
        forecastsArray.value[dayIndex].error = err.response?.data?.message || 'Failed to load content'
        forecastsArray.value[dayIndex].loading = false
      } else {
        currentEvents.value = null
        isEventsLoading.value = false
      }
    }
  }

  const clearAllForecasts = () => {
    forecastsArray.value = Array.from({ length: DAY_KEYS.length }, () => createEmptyForecast())
    summaryTextsArray.value = Array(DAY_KEYS.length).fill('')
  }

  const setAllForecastsLoading = () => {
    forecastsArray.value.forEach(f => { f.loading = true; f.error = null })
  }

  const buildPerDayCacheKey = (dateStr: string): string => {
    const location = opts.getLocation()
    const language = opts.getLanguage()
    const countryCode = (opts.getCountryCode() || '').trim()
    const model = (opts.getModelString() || DEFAULT_MODELS || '').trim()
    const llm = (opts.getSelectedLLM() || DEFAULT_LLM || '').trim()
    return `wital_weather_${location}_${countryCode}_${dateStr}_${language}_${opts.getTextLength()}_${opts.getSelectedAudience() || 'adult'}_${model}_${llm}`
  }

  const fetchWeeklyForecast = async () => {
    setAllForecastsLoading()

    const baseParams = {
      location: opts.getLocation(),
      country_code: (opts.getCountryCode() && opts.getCountryCode()!.trim()) ? opts.getCountryCode()!.trim() : undefined,
      model: opts.getModelString() || DEFAULT_MODELS,
    }

    // Phase 1: fetch weather data (no LLM, sub-second)
    try {
      const dataResp = await httpClient.get('/api/weekly-data', { params: baseParams })
      const data = dataResp.data

      if (data.location_info?.timezone) {
        opts.onTimezoneUpdate(data.location_info.timezone)
      }

      const forecasts: any[] = data.forecasts || []
      forecasts.forEach((day: any, index: number) => {
        if (index >= DAY_KEYS.length) return
        forecastsArray.value[index].csvData = (day.weather_data || '').toString()
        forecastsArray.value[index].metadata = {
          city_name: data.location_info?.location_name,
          country_code: data.location_info?.country_code,
          timezone: data.location_info?.timezone,
          latitude: data.location_info?.latitude,
          longitude: data.location_info?.longitude,
          sunrise: day.sunrise,
          sunset: day.sunset
        }
        // CSV is ready — mark loading false so icon/table views render instantly.
        // Text view will show empty until phase 2 completes.
        forecastsArray.value[index].loading = false
        forecastsArray.value[index].error = day.error || null
      })
    } catch (err: any) {
      console.error('❌ Weekly data failed:', err)
      forecastsArray.value.forEach(f => { f.loading = false; f.error = null })
    }

    // Phase 2: fetch forecast text in background (parallel LLM calls, ~5s).
    // If a summaryLength is configured (main view at a fixed length), also
    // kick off a parallel fetch at that length and populate summaryTextsArray.
    isWeeklyTextInFlight.value = true
    const summaryLen = opts.getSummaryLength?.() ?? null
    const primaryLen = opts.getTextLength()
    const needSummary = summaryLen != null && summaryLen !== primaryLen
    try {
      const buildTextParams = (length: number) => ({
        ...baseParams,
        language: opts.getLanguage(),
        length,
        audience: opts.getSelectedAudience() || 'adult',
        llm: opts.getSelectedLLM() || DEFAULT_LLM
      })

      const primaryPromise = httpClient.get('/api/weekly-text', { params: buildTextParams(primaryLen) })
      const summaryPromise = needSummary
        ? httpClient.get('/api/weekly-text', { params: buildTextParams(summaryLen!) })
        : Promise.resolve(null)

      const [textResp, summaryResp] = await Promise.all([primaryPromise, summaryPromise])
      const textData = textResp.data

      const forecasts: any[] = textData.forecasts || []
      forecasts.forEach((day: any, index: number) => {
        if (index >= DAY_KEYS.length) return
        if (day.message) {
          forecastsArray.value[index].text = day.message.toString().trim()
          forecastsArray.value[index].attribution = buildAttribution()
        }

        // Cache the full per-day entry so displayContent() finds cache hits on tab switch
        try {
          const perDayCacheKey = buildPerDayCacheKey(day.date)
          cacheManager.set(perDayCacheKey, {
            message: day.message,
            weather_data: forecastsArray.value[index].csvData,
            location_info: textData.location_info,
            sunrise: forecastsArray.value[index].metadata?.sunrise,
            sunset: forecastsArray.value[index].metadata?.sunset
          })
        } catch {}
      })

      if (needSummary && summaryResp) {
        const summaryForecasts: any[] = (summaryResp.data.forecasts || [])
        summaryForecasts.forEach((day: any, index: number) => {
          if (index >= DAY_KEYS.length) return
          if (day.message) summaryTextsArray.value[index] = day.message.toString().trim()
        })
      } else if (!needSummary) {
        // Summary length matches primary (or no summary configured) — mirror
        // primary texts so template code can read summaryTextsArray uniformly.
        summaryTextsArray.value = forecastsArray.value.map(f => f.text)
      }
    } catch (err: any) {
      console.error('❌ Weekly text failed, per-day fallback on tab switch:', err)
    } finally {
      isWeeklyTextInFlight.value = false
    }
  }

  return {
    forecastsArray,
    summaryTextsArray,
    currentEvents,
    currentEventType,
    isEventsLoading,
    eventsError,
    isWeeklyTextInFlight,
    displayContent,
    clearAllForecasts,
    setAllForecastsLoading,
    fetchWeeklyForecast
  }
}
